import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, size, replace, cloneDeep, isNull, isUndefined } from "lodash";

import shipmentTxnService from "@plannel/services/data/shipment-txn-service";
import locationService from "@plannel/services/data/location-service"
import customerService from "@plannel/services/data/customer-service";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import DateCondition from "@plannel/components/DateCondition";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridDatePicker from "@plannel/components/GridDatePicker"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const ShipmentTransactionMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);

  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? { originLocation: null, destLocation: null });
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [shipmentTxn, setShipmentTxn] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);

  const { snackBarMessageColor } = ColorUtil;
  const { shipmentCondition } = CodeUtil;

  const CURRENT_DATE = new ZDate();
  const MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
  const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);
  const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
  const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 3, 0);
  const initPeriod = { start: reduxViewState?.period ? new ZDate(reduxViewState.period.start) : START_DATE, end: reduxViewState?.period ? new ZDate(reduxViewState.period.end) : END_DATE };
  const [period, setPeriod] = useState(initPeriod);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? "eta");
  
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const [csvColDef, setCsvColDef] = useState([]);

  const EXCEL_EXPORT = "excelExport";
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dropdownValueFormatter = (params) => {
    return params?.value?.name ? params.value.name : "";
  };

  const objectComparator = (value1, value2, isDescending) => {
    const val1 = !value1 ? "" : value1.name;
    const val2 = !value2 ? "" : value2.name;
    
    return nullSafeComparator(val1, val2, isDescending);
  };

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.originLocation?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }
    
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const columnDefs = [
    {
      headerName: "originLocation", field: "originLocation",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      width: 130, 
      filterType: "string",
    },
    {
      headerName: "destLocation", field: "destLocation",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      width: 130, 
      filterType: "string",
    },
    { headerName: "customerCd", field: "customer.customerCd", colId: "customerCd", width: 130, type: ["nonEditableColumn"], cellClass: 'stringType', filterType: "string" },
    {
      headerName: "customerName", field: "customer", colId: "customerName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: customers
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      minWidth: 130, 
      filterType: "string",
    },
    { headerName: "categoryName", field: "categoryName", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 260, filterType: "string" },
    { headerName: "shipmentQty", field: "shptQty", type: ["numberColumn", "rightAligned"], width: 130, filterType: "number" },
    { headerName: "shipmentAmt", field: "shptAmt", type: ["numberColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 130, filterType: "number" },
    { headerName: "etd", field: "etd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "atd", field: "atd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "eta", field: "eta", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "ata", field: "ata", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "orderNum", field: "orderNum", width: 150, filterType: "string" },
    /*
      지연 파생 3 컬럼 — 테이블에 없는 **조회 시 계산값**이라 전부 읽기 전용이다.
        · 지연일수 둘은 **표시 전용**(filterType 없음) — 필터로 쓰려면 연산자 7 개를 각각 조립하는
          커스텀 핸들러가 필요한데, 지금 필요한 건 "유형으로 추리기" 뿐이라 거기까진 안 한다.
        · 지연 유형만 `select` 로 필터 가능 — 연산자가 equals 하나라 서버 핸들러가 단순하다.
          Supply Trend 의 지연 원인 분해에서 막대를 누르면 이 조건으로 이 화면이 열린다.
          ⚠ 서버 ColumnEnum(MasterColumn.ShipmentTransaction.DELAY_TYPE) 등록이 빠지면 조건이
            **조용히 무시**된다(advanced-filter 스킬 §6).
    */
    { headerName: "delayType", field: "delayType", type: ["nonEditableColumn"], width: 150,
      filterType: "select", filterOption: ["onTime", "depRecovered", "transitLate", "bothLate"],
      // 갈래 라벨은 Supply Trend 와 **같은 i18n 키**를 쓴다 — 두 화면이 다른 말을 쓰면 드릴이 끊긴다
      valueFormatter: (p) => (p.value ? t(`stBucket_${p.value}`) : "") },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp', width: 170 },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (locationSearch?.originLocation) {
      searchFilters.originLocCd = locationSearch.originLocation.locCd;
    }

    if (locationSearch?.destLocation) {
      searchFilters.destLocCd = locationSearch.destLocation.locCd;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

     if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (selectedSearch) {
      searchFilters.condition = selectedSearch
    }

    if (period?.start) {
      searchFilters.startDate = period?.start
    }

    if (period?.end) {
      searchFilters.endDate = period?.end
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveShipmentTxn = () => {
    const params = getRequestParams();
    shipmentTxnService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, isLimit, limitSize, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setLimitInfo({ isLimit, limitSize });
        }
        
        setShipmentTxn(results);
        setTotalPages(totalPages);
        setResultTotalCount(totalItems);
        if (size(currency)) {
          currencyMap.current = currency;
        }        
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxObject = { period, selectedSearch, locationSearch, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters };
        const reduxState = reduxUtil.createObjectState(viewName, reduxObject);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  };

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { data } = response;
        const { results } = data;

        setLocations(results);
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveCustomer = () => {
    customerService.getLookup()
      .then(response => {
        const { results } = response.data;
        const filteredResults = results.filter((result) => {
          return result.activeFlg === true && result.delFlg === false;
        });
        
        setCustomers(filteredResults);
      })
      .catch(e => {
        debug(e);
      })
  };

  const onGridReady = (params) => {
    retrieveLocation();
    retrieveCustomer();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const removeShipmentTxn = () => {
    const shipmentTxnIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        shipmentTxnIds.push(data.id)
      }
    );

    if (shipmentTxnIds.length > 0) {
      shipmentTxnService.remove(shipmentTxnIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveShipmentTxn();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  };

  const saveShipmentTxn = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      shipmentTxnService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .finally(() => {
          retrieveShipmentTxn();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveShipmentTxn();
    } else if (action === "DELETE") {
      removeShipmentTxn();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveShipmentTxn();
    }
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (csvColDef.isEmpty) {
      return;
    }
    for (const col of csvColDef) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => currencyRenderer(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvColDef]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveShipmentTxn();
    }

    return () => {
      setShipmentTxn([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setShipmentTxn([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch.originLocation, locationSearch.destLocation]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setShipmentTxn([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, period, selectedSearch]);

  useEffect(() => {
    if (limitInfo.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({ viewName: `${viewName}_NOTIFY`, showLimitSnackbar: false }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo.isLimit, showLimitSnackbar.current]);

  useEffect(() => {
    if (!isEmpty(locations) || !isEmpty(customers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
      setCsvColDef(cloneDeep(columnDefs))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, customers]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      originLocation: null,
      destLocation: null,
      avgLtDays: 0,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <DateCondition
          conditionList={shipmentCondition}
          period={period}
          maxDate={MAX_DATE}
          minDate={MIN_DATE}
          selectedSearch={selectedSearch}
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriod}
          useAutoComplete={false}
          views={['year', 'month']}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.originLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, originLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ mr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("originLocation")} />
          )}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.destLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, destLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ mr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("destLocation")} />
          )}
        />
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} sx={{ mr: 1, width: 250 }} extraCodesRef={extraCustomerCodesRef} />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ width: 250 }} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={shipmentTxnService} columnDefs={csvColDef} totalRows={resultTotalCount} params={getRequestParams(false)} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="shipment-transaction-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={shipmentTxn}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              statusBar={statusBar}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ShipmentTransactionMaster);